import Coordinator from './models/coordinator';
import SmartSelectGroup from './models/smart-select-group';
import { computeRowsAndColumnsAfterTidying, fillElementToGrid, getGrid } from './smart-select.helper';

export function distributeHorizontally (smartSelectGroup) {
  if (smartSelectGroup instanceof SmartSelectGroup) {
    const leftMostElement = smartSelectGroup.getLeftMostElement();
    const otherElements = smartSelectGroup.getBetweenLeftAndRightElements();

    const distanceBetweenLeftMostAndRightMostElement = smartSelectGroup.getDistanceBetweenLeftMostAndRightMostElement();

    const totalWidthOfOtherElements = otherElements.reduce((acc, elem) => acc + elem.getCoordinator().width, 0);

    const totalRemainingSpace = distanceBetweenLeftMostAndRightMostElement - totalWidthOfOtherElements;

    const gap = totalRemainingSpace / (otherElements.length + 1);

    const otherElementsWithNewCoordinator = otherElements.map(
      (currentElement) => ({
        element: currentElement,
        newCoordinator: currentElement.getCoordinator().clone()
      })
    );

    return otherElementsWithNewCoordinator.map((currentElement, i) => {
      const newCoordinator = currentElement.newCoordinator;
      if (i === 0) {
        newCoordinator.left = leftMostElement.getCoordinator().getRight() + gap;
      } else {
        newCoordinator.left = otherElementsWithNewCoordinator[i - 1].newCoordinator.getRight() + gap;
      }

      return currentElement;
    });
  }

  return [];
}

export function distributeVertically (smartSelectGroup) {
  if (smartSelectGroup instanceof SmartSelectGroup) {
    const topMostElement = smartSelectGroup.getTopMostElement();
    const otherElements = smartSelectGroup.getBetweenTopAndBottomElements();

    const distanceBetweenTopMostAndBottomMostElement = smartSelectGroup.getDistanceBetweenTopMostAndBottomMostElement();

    const totalHeightOfOtherElements = otherElements.reduce((acc, elem) => acc + elem.getCoordinator().height, 0);

    const totalRemainingSpace = distanceBetweenTopMostAndBottomMostElement - totalHeightOfOtherElements;

    const gap = totalRemainingSpace / (otherElements.length + 1);

    const otherElementsWithNewCoordinator = otherElements.map(
      (currentElement) => ({
        element: currentElement,
        newCoordinator: currentElement.getCoordinator().clone()
      })
    );

    return otherElementsWithNewCoordinator.map((currentElement, i) => {
      const newCoordinator = currentElement.newCoordinator;
      if (i === 0) {
        newCoordinator.top = topMostElement.getCoordinator().getBottom() + gap;
      } else {
        newCoordinator.top = otherElementsWithNewCoordinator[i - 1].newCoordinator.getBottom() + gap;
      }

      return currentElement;
    });
  }

  return [];
}

export function tryTidyVertically (smartSelectGroup) {
  if (smartSelectGroup instanceof SmartSelectGroup) {
    const mostCommonGap = smartSelectGroup.getMostCommonVerticalGap();

    const elementsWithNewCoordinator = smartSelectGroup.getTopToBottomElements().map(
      (currentElement) => ({
        element: currentElement,
        newCoordinator: currentElement.getCoordinator().clone()
      })
    );

    const midIndex = Math.floor(elementsWithNewCoordinator.length / 2);

    const updatedElements = [];

    // calculate position for top half elements
    for (let i = midIndex - 1; i >= 0; i--) {
      const currentElement = elementsWithNewCoordinator[i];
      const nextElement = elementsWithNewCoordinator[i + 1];

      const newCoordinator = currentElement.newCoordinator;
      newCoordinator.top = nextElement.newCoordinator.top - mostCommonGap - newCoordinator.height;

      updatedElements.push(currentElement);
    }

    // calculate position for bottom half elements
    for (let i = midIndex + 1; i < elementsWithNewCoordinator.length; i++) {
      const currentElement = elementsWithNewCoordinator[i];
      const previousElement = elementsWithNewCoordinator[i - 1];

      const newCoordinator = currentElement.newCoordinator;
      newCoordinator.top = previousElement.newCoordinator.getBottom() + mostCommonGap;

      updatedElements.push(currentElement);
    }

    return updatedElements;
  }

  return null;
}

export function tryTidyHorizontally (smartSelectGroup) {
  if (smartSelectGroup instanceof SmartSelectGroup) {
    const elementsWithNewCoordinator = smartSelectGroup.getLeftToRightElements().map(
      (currentElement) => ({
        element: currentElement,
        newCoordinator: currentElement.getCoordinator().clone()
      })
    );

    const mostCommonGap = smartSelectGroup.getMostCommonHorizontalGap();

    const midIndex = Math.floor(elementsWithNewCoordinator.length / 2);

    const updatedElements = [];

    // calculate position for left half elements
    for (let i = midIndex - 1; i >= 0; i--) {
      const currentElement = elementsWithNewCoordinator[i];
      const nextElement = elementsWithNewCoordinator[i + 1];

      const coordinator = currentElement.newCoordinator;
      coordinator.left = nextElement.newCoordinator.left - mostCommonGap - coordinator.width;

      updatedElements.push(currentElement);
    }

    // calculate position for right half elements
    for (let i = midIndex + 1; i < elementsWithNewCoordinator.length; i++) {
      const currentElement = elementsWithNewCoordinator[i];
      const previousElement = elementsWithNewCoordinator[i - 1];

      const coordinator = currentElement.newCoordinator;
      coordinator.left = previousElement.newCoordinator.getRight() + mostCommonGap;

      updatedElements.push(currentElement);
    }

    return updatedElements;
  }

  return null;
}

export function isTidied (smartSelectGroup) {
  if (smartSelectGroup instanceof SmartSelectGroup) {
    const elements = smartSelectGroup.getLeftToRightElements();

    const leftDistanceBetweenElements = [...new Set(elements.map((elem, index) => {
      if (index === 0) {
        return undefined;
      } else {
        return Math.floor(Math.round(elem.getCoordinator().left) - Math.round(elements[index - 1].getCoordinator().left));
      }
    }).filter((gap) => gap !== undefined))];

    if (leftDistanceBetweenElements.length < 1) {
      return true;
    }

    if (leftDistanceBetweenElements.length === 2) {
      return leftDistanceBetweenElements[0] === 0 || leftDistanceBetweenElements[1] === 0;
    }

    return false;
  }

  return false;
}

export function tidy (smartSelectGroup) {
  if (smartSelectGroup instanceof SmartSelectGroup) {
    if (isTidied(smartSelectGroup)) {
      return [];
    }

    let grid, cellWidth, cellHeight, numColumns, numRows;

    ({ numColumns, numRows } = computeRowsAndColumnsAfterTidying(smartSelectGroup));

    if (numColumns === 1) {
      return distributeVertically(smartSelectGroup);
    }

    if (numRows === 1) {
      return distributeHorizontally(smartSelectGroup);
    }

    ({ grid, cellHeight, cellWidth } = getGrid(
      numRows,
      numColumns,
      smartSelectGroup.getMaxLeftDistance(),
      smartSelectGroup.getMaxTopDistance()
    ));

    ({ grid, cellWidth, cellHeight, numColumns, numRows } = fillElementToGrid({
      smartSelectGroup,
      grid,
      cellWidth,
      cellHeight
    }));

    const updatedElements = [];

    // calculate new coordinators
    for (let row = 0; row < numRows; row++) {
      for (let column = 0; column < numColumns; column++) {
        if (grid[row][column].element) {
          const newCoordinator = new Coordinator({
            x: Math.ceil(grid[row][column].left + smartSelectGroup.getLeftBorder()),
            y: Math.ceil(grid[row][column].top + smartSelectGroup.getTopBorder()),
            width: cellWidth,
            height: cellHeight
          });

          updatedElements.push({
            element: grid[row][column].element,
            newCoordinator
          });
        }
      }
    }

    return updatedElements;
  }

  return [];
}
